import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const AccountManagement = ({ settings, onChange }) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportFormatOptions = [
    { value: 'json', label: 'JSON Format', description: 'Machine-readable JSON file' },
    { value: 'csv', label: 'CSV Format', description: 'Spreadsheet-compatible format' },
    { value: 'pdf', label: 'PDF Report', description: 'Human-readable PDF document' }
  ];

  const connectedDApps = [
    { name: 'PolyNotary', status: 'active', lastUsed: '2 minutes ago', permissions: ['Read wallet address', 'Request transactions'] },
    { name: 'MetaMask', status: 'active', lastUsed: '5 minutes ago', permissions: ['Wallet connection', 'Network switching'] },
    { name: 'OpenSea', status: 'inactive', lastUsed: '2 days ago', permissions: ['Read NFTs', 'Marketplace access'] }
  ];

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      // Simulate export process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Create mock export data
      const exportData = {
        account: {
          walletAddress: '0x742d35Cc6634C0532925a3b8D4C9db96590b5b8c',
          exportDate: new Date()?.toISOString(),
          totalArchives: 127,
          storageUsed: '245 MB'
        },
        archives: [
          {
            id: 1,
            url: 'https://www.polygon.technology',
            timestamp: '2025-01-07T09:02:27.704Z',
            ipfsHash: 'QmYjtig7VJQ6XsnUjqqJvj7QaMcCAwtrgNdahSiFofrE7o',
            transactionHash: '0x742d35cc6634c0532925a3b8d4c9db96590b5b8c'
          }
        ]
      };

      // Download file based on format
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `polynotary-export-${new Date()?.toISOString()?.split('T')?.[0]}.${settings?.exportFormat}`;
      document.body?.appendChild(a);
      a?.click();
      document.body?.removeChild(a);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleRevokeDApp = (dappName) => {
    // In a real app, this would revoke permissions for the dApp
    console.log(`Revoking permissions for ${dappName}`);
  };

  return (
    <div className="space-y-6">
      {/* Data Export */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="Download" size={18} className="text-primary" />
          <h3 className="text-lg font-medium text-foreground">Data Export</h3>
        </div>
        
        <div className="space-y-4">
          <Select
            label="Export Format"
            value={settings?.exportFormat}
            onChange={(value) => onChange('exportFormat', value)}
            options={exportFormatOptions}
            description="Choose the format for your exported data"
          />
          
          <Button
            iconName="Download"
            loading={isExporting}
            onClick={handleExportData}
            className="w-full md:w-auto"
          >
            {isExporting ? 'Preparing Export...' : 'Export Transaction History'}
          </Button>
        </div>
      </div>

      {/* Account Information */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="User" size={18} className="text-primary" />
          <h3 className="text-lg font-medium text-foreground">Account Information</h3>
        </div>
        
        <div className="bg-muted rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Wallet Address</p>
              <p className="text-sm font-mono text-foreground">0x742d...5b8c</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Account Created</p>
              <p className="text-sm text-foreground">January 7, 2025</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Archives</p>
              <p className="text-sm text-foreground">127 webpages</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Storage Used</p>
              <p className="text-sm text-foreground">{settings?.storageUsed || '245 MB'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Backup & Recovery */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={18} className="text-primary" />
          <h3 className="text-lg font-medium text-foreground">Backup & Recovery</h3>
        </div>
        
        <div className="space-y-4">
          <div className="bg-muted rounded-lg p-4">
            <Checkbox
              checked={settings?.backupReminders}
              onChange={(e) => onChange('backupReminders', e?.target?.checked)}
              label="Backup Reminders"
              description="Get reminded to backup your wallet and transaction history"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-muted rounded-lg p-4 text-center">
              <Icon name="Key" size={24} className="text-orange-600 mx-auto mb-2" />
              <h4 className="font-medium text-foreground mb-1">Seed Phrase</h4>
              <p className="text-xs text-muted-foreground mb-3">Backup your wallet seed phrase</p>
              <Button variant="outline" size="sm" fullWidth>
                View Seed Phrase
              </Button>
            </div>
            <div className="bg-muted rounded-lg p-4 text-center">
              <Icon name="FileText" size={24} className="text-blue-600 mx-auto mb-2" />
              <h4 className="font-medium text-foreground mb-1">Archive Backup</h4>
              <p className="text-xs text-muted-foreground mb-3">Download all your archives</p>
              <Button variant="outline" size="sm" fullWidth>
                Create Backup
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Connected dApps */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="Link" size={18} className="text-primary" />
          <h3 className="text-lg font-medium text-foreground">Connected dApps</h3>
        </div>
        
        <div className="space-y-3">
          {connectedDApps?.map((dapp, index) => (
            <div key={index} className="bg-muted rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    dapp?.status === 'active' ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    <Icon name="Globe" size={20} className={
                      dapp?.status === 'active' ? 'text-green-600' : 'text-gray-600'
                    } />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-foreground">{dapp?.name}</h4>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        dapp?.status === 'active' ?'bg-green-100 text-green-700' :'bg-gray-100 text-gray-600'
                      }`}>
                        {dapp?.status}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">Last used: {dapp?.lastUsed}</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  iconName="X"
                  onClick={() => handleRevokeDApp(dapp?.name)}
                >
                  Revoke
                </Button>
              </div>
              
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2">Permissions:</p>
                <div className="flex flex-wrap gap-2">
                  {dapp?.permissions?.map((permission, idx) => (
                    <span 
                      key={idx}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded"
                    >
                      {permission}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AccountManagement;